/**
 * <s-product-gallery> — main-image viewer with selectable thumbnails.
 *
 * Required markup:
 *   [data-gallery-track]               — horizontal scroll-snap track (native swipe on touch).
 *   [data-gallery-slide]               — one per media item, in track order.
 *   button[command="--show"][data-index] — thumbnail invokers targeting this element's id.
 *
 * Slides, thumbnails and the track are always read from the current DOM, never cached,
 * because a page refresh or morph can rebuild or reorder them while this element is reused.
 *
 * Optional (data-variant-media-sync, leash only):
 *   data-product-id        — matches the [data-product-form] that owns input[name="id"].
 *   data-variant-media-map — "variantId:mediaId," pairs; slides carry data-media-id.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #observer;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  get #track() {
    return this.querySelector('[data-gallery-track]');
  }

  get #slides() {
    return Array.from(this.querySelectorAll('[data-gallery-slide]'));
  }

  get #thumbs() {
    return Array.from(this.querySelectorAll('button[command="--show"][data-index]'));
  }

  connectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    if (!this.#track || this.#slides.length < 2) return;

    this.addEventListener('command', (event) => {
      if (event.command !== '--show') return;
      const index = Number(event.source?.dataset.index);
      if (Number.isInteger(index)) this.show(index);
    }, opts);

    // Arrow keys move between thumbnails, matching a toolbar-style group.
    this.addEventListener('keydown', (event) => {
      const thumbs = this.#thumbs;
      const current = thumbs.indexOf(document.activeElement);
      if (current === -1) return;
      const rtl = getComputedStyle(this).direction === 'rtl';
      let next = null;
      if (event.key === 'ArrowRight') next = current + (rtl ? -1 : 1);
      if (event.key === 'ArrowLeft') next = current + (rtl ? 1 : -1);
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = thumbs.length - 1;
      if (next === null) return;
      event.preventDefault();
      next = (next + thumbs.length) % thumbs.length;
      thumbs[next].focus();
      this.show(next);
    }, opts);

    this.#observeSlides();

    // Opt-in per product: follow the chosen variant's image after the product form updates.
    if (this.hasAttribute('data-variant-media-sync')) this.#syncVariantMedia(opts);
  }

  // Swiping the track keeps the active thumbnail in sync; re-run whenever slides are replaced.
  #observeSlides() {
    this.#observer?.disconnect();
    const track = this.#track;
    if (!track) return;
    this.#observer = new IntersectionObserver((entries) => {
      if (this.#syncLock) return;
      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          this.#setActive(this.#slides.indexOf(entry.target));
        }
      }
    }, { root: track, threshold: [0.6] });
    this.#slides.forEach((slide) => this.#observer.observe(slide));
  }

  #syncLock = false;

  #syncVariantMedia({ signal }) {
    const productId = this.dataset.productId;
    const findForm = () => document.querySelector(`[data-product-form][data-product-id="${productId}"]`);
    if (!findForm()) return;

    // URLs without a variant keep the demo video until a color is actually chosen.
    let hasSelection = new URL(location.href).searchParams.has('variant');

    const targetMediaId = () => {
      const form = findForm();
      if (!form) return '';
      const variantId = form.querySelector('input[name="id"]')?.value || '';
      const map = new Map(
        (this.dataset.variantMediaMap || '')
          .split(',')
          .filter(Boolean)
          .map((pair) => pair.split(':'))
      );
      return map.get(variantId)
        || form.querySelector('[data-variant-media-id]')?.dataset.variantMediaId
        || '';
    };

    // Always resolve the media ID against the current slides, so a reorder never leaves a stale index.
    const sync = () => {
      if (!hasSelection) return;
      const mediaId = targetMediaId();
      if (!mediaId) return;
      const slides = this.#slides;
      const index = slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
      if (index === -1) return;
      const activeIndex = slides.findIndex((slide) => !slide.hasAttribute('inert'));
      if (index === activeIndex && this.#isInView(slides[index])) return;
      this.show(index, { instant: true });
    };

    // Coalesce bursts of DOM updates and run after the form refresh/morph has settled.
    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(sync);
      });
    };

    // Watch the nearest ancestor holding both the gallery and the form, so replaced nodes stay covered.
    let root = this.parentElement;
    while (root && !root.contains(findForm())) root = root.parentElement;
    root = root || document.body;

    const onChange = (event) => {
      const form = findForm();
      if (!form || !form.contains(event.target)) return;
      hasSelection = true;
      schedule();
    };
    root.addEventListener('change', onChange, { signal });

    const mutationObserver = new MutationObserver((records) => {
      const galleryChanged = records.some((record) => this.contains(record.target) || record.target === this);
      if (galleryChanged) this.#observeSlides();
      schedule();
    });
    mutationObserver.observe(root, { childList: true, subtree: true });
    mutationObserver.observe(this, { attributes: true, attributeFilter: ['data-variant-media-map'] });

    signal.addEventListener('abort', () => {
      cancelAnimationFrame(frame);
      mutationObserver.disconnect();
    });

    if (hasSelection) schedule();
  }

  #isInView(slide) {
    const track = this.#track;
    if (!track || !slide) return false;
    const trackRect = track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    return Math.abs(slideRect.left - trackRect.left) < 2;
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
  }

  show(index, { instant = false } = {}) {
    const track = this.#track;
    const slide = this.#slides[index];
    if (!track || !slide) return;
    const behavior = instant || this.#reducedMotion.matches ? 'auto' : 'smooth';
    const trackRect = track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    if (instant) {
      // Hold off swipe tracking so an in-flight observer callback can't re-activate an old slide.
      this.#syncLock = true;
      requestAnimationFrame(() => requestAnimationFrame(() => { this.#syncLock = false; }));
    }
    track.scrollBy({ left: slideRect.left - trackRect.left, behavior });
    this.#setActive(index);
  }

  #setActive(index) {
    if (index < 0) return;
    this.#thumbs.forEach((thumb, i) => {
      if (i === index) {
        thumb.setAttribute('aria-current', 'true');
        // Scroll only the thumbnail strip horizontally so the page never jumps vertically.
        const strip = thumb.closest('[data-gallery-thumbs]');
        if (strip) {
          const stripRect = strip.getBoundingClientRect();
          const thumbRect = thumb.getBoundingClientRect();
          if (thumbRect.left < stripRect.left || thumbRect.right > stripRect.right) {
            strip.scrollBy({ left: thumbRect.left - stripRect.left - (stripRect.width - thumbRect.width) / 2 });
          }
        }
      } else {
        thumb.removeAttribute('aria-current');
      }
    });
    this.#slides.forEach((slide, i) => slide.toggleAttribute('inert', i !== index));
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
